import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ResponsiveLine } from '@nivo/line';
import { Row, Col, Card, CardBody, CardTitle } from 'reactstrap';
import { loadAccountingStats } from '../../redux';
import { Dropdown } from 'semantic-ui-react';

const AccountingLineChart = ({
    chartDataBuilder,
    filters = {},
    icon = 'fa-chart-line',
    unit = 'chf',
    colorScheme = 'nivo',
    onClickOnNode
}) => {
    const HASH = `trend`;

    const { t }    = useTranslation();
    const dispatch = useDispatch();

    const [groupBy, setGroupBy]     = useState('day')
    const [compareTo, setCompareTo] = useState(null)

    const stats = useSelector(state => state.accountingStats.stats)[HASH];

    useEffect(() => {
        dispatch(loadAccountingStats(HASH, {
            ...filters,
            group_by: groupBy,
            compare_to: compareTo
        }));
    }, [dispatch, filters, HASH, groupBy, compareTo]);

    let lineChartData = useMemo(() => stats?.data?.current?.results?.map(result => {
        return  {
            _id: result[groupBy],
            id: result[groupBy],
            value: result.total_amount,
            count: parseInt(result.count, 10)
        };
    }), [stats]);
    const totalCount = useMemo(() => (
        lineChartData?.reduce((sum, item) => sum + item.count, 0)
    ), [stats]);
    const totalValue = useMemo(() => (
        lineChartData?.reduce((sum, item) => sum + item.value, 0)
    ), [stats]);

    const title = t(`accounting.stats.${HASH}.title`);

    const onClick = (node, event) => {
        onClickOnNode(node.data._id);
    };

    const groupByOptions = [
        'type',
        'tag', 'tags',
        'cashregister',
        'author',
        'payment_method',
        'pricing',
        'year',
        'month', 'month_of_year',
        'week', 'week_of_year',
        'day', 'day_of_week', 'day_of_month', 'day_of_year',
        'hour', 'hour_of_day',
        'half_hour', 'half_hour_of_day',
        'quarter_hour', 'quarter_hour_of_day'
    ].map(property => ({
        key: property,
        text: t(`accounting.filters.group_by_${property}`),
        value: property
    }));

    const compareToOptions = [
        {key: 'last-year',  text: t('accounting.filters.compare_to_last_year'), value: 'last-year'},
        {key: 'last-month', text: t('accounting.filters.compare_to_last_month'), value: 'last-month'},
        {key: 'last-week',  text: t('accounting.filters.compare_to_last_week'), value: 'last-week'},
        {key: 'last-day',  text: t('accounting.filters.compare_to_last_week'), value: 'last-day'}
    ];

    const d = [{
        "id": "current",
        "data": stats?.data?.current?.results?.map(result => ({ x: result[groupBy], y: result.total_amount }))
    }];

    return (
        <Card className="mb-4">
            <CardBody>
                <CardTitle>
                    <i className={`fa ${icon}`}></i> {title}
                </CardTitle>

                <Row>
                    <Col xs={12} sm={6}>
                        <label>{t('accounting.filters.group_by')}</label>
                        <div>
                            <Dropdown
                                options={groupByOptions}
                                placeholder={t('accounting.filters.select_grouping_options')}
                                defaultValue={groupBy}
                                onChange={(e, data) => setGroupBy(data.value)}
                                className="w-100"
                                search
                                selection
                            />
                        </div>
                    </Col>
                    <Col xs={12} sm={6}>
                        <label>{t('accounting.filters.compare_to')}</label>
                        <div>
                            <Dropdown
                                options={compareToOptions}
                                defaultValue={compareTo}
                                onChange={(e, data) => setCompareTo(data.value)}
                                className="w-100"
                                placeholder={t('accounting.filters.no_comparison')}
                                search
                                selection
                            />
                        </div>
                    </Col>
                </Row>

                {stats?.isLoading ? (
                    <div className="text-center py-5">
                        <i className="fa fa-spinner fa-spin fa-2x"></i>
                        <p className="mt-2">{t('accounting.stats.loading_data')}</p>
                    </div>
                ) : (stats?.error ? (
                    <div className="text-center py-5 text-danger">
                        <i className="fa fa-exclamation-triangle fa-2x"></i>
                        <p className="mt-2">{stats?.error}</p>
                    </div>
                ) : (!lineChartData?.length ? (
                    <div className="text-center py-5">
                        <p>{t('accounting.stats.no_data')}</p>
                    </div>
                ) : (
                    <>
                        <div className="mb-3">
                            <p className="mb-1"><strong>{t('accounting.stats.nb_of_transactions')}:</strong> {totalCount}</p>
                            <p className="mb-1"><strong>{t('accounting.stats.total_amount')}:</strong> {totalValue.toFixed(2)} {unit}</p>
                        </div>
                        <div style={{height: 400}}>
                            <ResponsiveLine
                                data={d}
                                margin={{ top: 50, right: 110, bottom: 50, left: 60 }}
                                yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: true, reverse: false }}
                                axisBottom={{ legend: 'transportation', legendOffset: 36 }}
                                axisLeft={{ legend: 'count', legendOffset: -40 }}
                                pointSize={10}
                                pointColor={{ theme: 'background' }}
                                pointBorderWidth={2}
                                pointBorderColor={{ from: 'seriesColor' }}
                                pointLabelYOffset={-12}
                                enableTouchCrosshair={true}
                                useMesh={true}
                                legends={[
                                    {
                                        anchor: 'bottom-right',
                                        direction: 'column',
                                        translateX: 100,
                                        itemWidth: 80,
                                        itemHeight: 22,
                                        symbolShape: 'circle'
                                    }
                                ]}
                            />
                        </div>
                    </>
                )))}
            </CardBody>
        </Card>
    );
};

export default AccountingLineChart;
